/**
 * Видимое окно карты: канвас всегда во весь экран, но его накрывают камера AR
 * сверху и панель ведения снизу. Оптический центр карты обязан быть в том
 * окне, которое реально видно (запрос владельца 04.08) — иначе «вы здесь» и
 * маршрут уезжают под камеру.
 *
 * Синхронизатор ОДИН на приложение: раньше отступы ставили и AR-оверлей, и
 * старт ведения, и они перетирали друг друга (после смены цели в AR центр
 * возвращался под камеру). Здесь состояние считается целиком из сигналов, а
 * значит любое их изменение приводит карту в согласованный вид.
 */
import { useEffect } from 'preact/hooks'
import type { ViewInsets } from './map/camera-fit'
import {
  appState,
  arMode,
  arSplitRatio,
  cameraHeld,
  compassOwnsBearing,
  followDetent,
  navProgress,
  venueMap,
} from './state'

/** Свёрнутая строка ведения + запас над кромкой экрана. */
export const FOLLOW_BAR_PX = 84
/** Строка ведения и сегмент режимов друг над другом (AR-сессия). */
export const AR_BOTTOM_PX = 132

export interface InsetInputs {
  /** Режим AR-оверлея; null — оверлея нет. */
  readonly mode: 'ar' | 'split' | 'map' | null
  /** Доля экрана под камеру в режиме «камера и карта». */
  readonly splitRatio: number
  /** Идёт ведение (внизу строка маршрута). */
  readonly following: boolean
  /** Панель ведения убрана в ручку — снизу почти ничего не перекрыто. */
  readonly panelHidden: boolean
  readonly viewportHeightPx: number
}

/** Чистый расчёт: сколько сверху и снизу закрыто в текущем состоянии. */
export function computeInsets(input: InsetInputs): ViewInsets {
  const { mode, splitRatio, following, panelHidden, viewportHeightPx } = input
  // Полный AR и «только карта» камерой не делятся: сверху ничего не закрыто.
  const top = mode === 'split' ? Math.round(viewportHeightPx * splitRatio) : 0
  if (panelHidden) return { top, bottom: mode === null ? 0 : AR_BOTTOM_PX - FOLLOW_BAR_PX }
  if (mode !== null) return { top, bottom: AR_BOTTOM_PX }
  return { top: 0, bottom: following ? FOLLOW_BAR_PX : 0 }
}

/**
 * Надо ли после смены видимого окна вернуть позицию в его середину.
 *
 * Отступы сами по себе только сдвигают оптический центр — то, что было в
 * центре, окажется в середине новой полосы. Но если человек уже отъехал
 * камерой (или мы просто стоим в обзоре), дёргать карту нельзя:
 *  – в обзоре камера вообще не следует за позицией, и рывок был бы
 *    необъяснимым: человек рассматривал угол площадки, а карта прыгнула;
 *  – во время ведения, когда карту держит рука, любое авто-движение отнимает
 *    у человека то, ради чего он её сдвинул (camera-hold.ts).
 * Значит: возвращаем только в ведении и только если карту не держат.
 */
export function shouldRecenterOnInsets(input: {
  readonly following: boolean
  readonly held: boolean
  readonly hasPosition: boolean
}): boolean {
  return input.following && !input.held && input.hasPosition
}

/**
 * Подписка: сигналы + изменение размеров окна (поворот телефона, сворачивание
 * адресной строки — отступы считаются в пикселях от высоты экрана).
 *
 * Смена окна — это не только цифры padding: появилась камера сверху или
 * человек потянул границу «камера/карта», и позиция обязана заново оказаться
 * в видимой полосе, иначе она остаётся под камерой (ровно то, ради чего
 * отступы и заводились).
 */
export function useMapInsets(): void {
  const map = venueMap.value
  const mode = arMode.value
  const ratio = arSplitRatio.value
  const state = appState.value
  const detent = followDetent.value

  useEffect(() => {
    if (!map) return
    const apply = (): void => {
      map.setViewInsets(
        computeInsets({
          mode,
          splitRatio: ratio,
          following: state === 'follow',
          panelHidden: detent === 'hidden',
          viewportHeightPx: window.innerHeight,
        }),
      )
      const progress = navProgress.peek()
      const position = progress?.position ?? null
      if (
        shouldRecenterOnInsets({
          following: state === 'follow',
          held: cameraHeld.peek(),
          hasPosition: position !== null,
        }) &&
        position
      ) {
        map.followCamera(position, compassOwnsBearing() ? null : (progress?.headingDeg ?? null))
      }
    }
    apply()
    window.addEventListener('resize', apply)
    window.addEventListener('orientationchange', apply)
    return () => {
      window.removeEventListener('resize', apply)
      window.removeEventListener('orientationchange', apply)
    }
  }, [map, mode, ratio, state, detent])
}
